/**
 * MONSTER TOWN — THE WAY IN. OWNED BY THE INTERIORS CREW.
 *
 * The front door standing in the town, and the portal wiring for every doorway
 * in the house. Split out of `house.ts` to keep both files under the 500-line
 * house rule.
 *
 * DOORS ARE PART OF THE BUILDING, NOT PLACEABLES. A door a kid can pick up in
 * build mode is a door a kid can delete themselves into a room with no exit.
 * A doorway is a gap in the shell with a bright mat in front of it; walking
 * onto the mat uses a `Portal`.
 */

import * as THREE from 'three'
import type { Cell } from '../core/grid'
import { world, type SceneNode } from '../core/scene'
import { boxOn, cone, cylinder, flat, group, inkAll } from '../render/kit'
import { ROOMS, TOWN_DOOR_CELL, TOWN_ID, doorCell, roomDef } from './rooms'

let townDoor: Cell = { ...TOWN_DOOR_CELL }
let porch: THREE.Group | null = null

export function townDoorCell(): Cell {
  return townDoor
}

/**
 * Move the way-in to the world crew's real house.
 *
 * ROUND-1 BLOCKER, FIXED HERE. This file used to raise a stand-in "house front"
 * in the middle of the kid's plot: a 4.6-wide wall slab with a 6.8-wide pyramid
 * roof floating on top of it. Because the roof was far wider than the only wall
 * beneath it, most of its span hung over nothing and you could see the path
 * straight through the gap between the roof underside and the wall top — the
 * critic's "floating canopy roof plus a door, with a see-through gap". It was
 * also a SECOND house: the world crew has always built a real, solid, cream
 * house with a red gable, a chimney, windows and planters across the road.
 *
 * So the stand-in is gone. The town's front door is the real house's front
 * door; all that remains here is the doorstep the portal sits on, laid flat on
 * the pavement in front of it. Nothing floats because nothing is raised.
 */
export function attachHouseEntrance(cell: Cell): void {
  townDoor = { ...cell }
  const node = world.get(TOWN_ID)
  if (node !== undefined) {
    if (porch !== null) node.statics.remove(porch)
    porch = buildDoorstep(node)
    node.statics.add(porch)
  }
  world.removePortalsFor(TOWN_ID)
  wirePortals()
}

/**
 * Boot: lay the doorstep and wire every doorway both ways.
 *
 * IDEMPOTENT. The world crew calls `attachHouseEntrance()` during its own
 * install, which runs first; without the remove below this would add a second
 * doorstep on top of the first.
 */
export function installEntrance(): void {
  const townNode = world.get(TOWN_ID)
  if (townNode !== undefined) {
    if (porch !== null) townNode.statics.remove(porch)
    porch = buildDoorstep(townNode)
    townNode.statics.add(porch)
  }
  world.removePortalsFor(TOWN_ID)
  wirePortals()
}

/**
 * The doorstep: a stone step, a bright welcome mat and two little bollard
 * lamps. Flat on the ground, under 40cm tall, so it can never hide the house
 * behind it and can never read as a floating anything.
 */
function buildDoorstep(node: SceneNode): THREE.Group {
  const t = node.grid.tileSize
  const c = node.grid.cellToWorld(townDoor)
  const g = group('houseDoorstep')
  g.position.set(c.x, 0, c.z)

  g.add(boxOn(t * 1.7, t * 0.1, t * 0.9, { color: 'rock' }))
  g.add(boxOn(t * 1.5, t * 0.05, t * 0.74, { color: 'pathEdge', y: t * 0.1 }))
  // The mat is the tap target and the thing the portal sits under. Warm orange
  // against grey stone: the one place on the ground a kid should aim at.
  g.add(boxOn(t * 1.1, t * 0.06, t * 0.44, { color: 'uiPrimary', y: t * 0.15, name: 'mat' }))
  g.add(boxOn(t * 0.86, t * 0.03, t * 0.3, { color: 'uiCoin', y: t * 0.21 }))
  /*
   * THE TWO LITTLE LAMPS, BUILT LIKE THE TOWN'S LAMPS.
   *
   * ROUND-3: "two unexplained white bollard blobs flanking the crossing". They
   * were a bare `metalDark` stalk under a bare `sunWarm` cone, with no ink
   * outline, no base and no cap — standing a couple of metres from street lamps
   * that have all three. Un-outlined pale geometry beside outlined geometry does
   * not read as a smaller thing; it reads as an unfinished thing. Same parts as
   * `lampPost()` now, at doorstep size: stone foot, dark stalk, warm glow, red
   * cap, everything inked except the glow itself (light is not silhouette).
   */
  for (const s of [-1, 1]) {
    const post = group('doorLamp')
    const x = s * t * 0.92
    const z = t * 0.1
    post.add(boxOn(t * 0.26, t * 0.07, t * 0.26, { color: 'rockDark', x, z }))
    post.add(cylinder(t * 0.07, t * 0.4, { color: 'metalDark', x, y: t * 0.06, z }))
    const cap = cone(t * 0.17, t * 0.16, { color: 'roofRed', x, y: t * 0.6, z, seg: 6 })
    post.add(cap)
    inkAll(post)
    // Added after the ink pass so the glow is never boxed in — see lampPost().
    const bulb = boxOn(t * 0.2, t * 0.16, t * 0.2, { color: 'sunWarm', x, y: t * 0.45, z })
    bulb.material = flat('sunWarm')
    bulb.castShadow = false
    post.add(bulb)
    g.add(post)
  }
  return g
}

/**
 * Every room declares its own outgoing doors, so wiring each room's list covers
 * both directions of an interior doorway. Only the town needs both sides added
 * here, because the town is not a room.
 */
function wirePortals(): void {
  /*
   * Clear first. `WorldGraph.addPortal` pushes onto an array with no dedupe, and
   * this now runs TWICE at boot — once when the world crew hands over the real
   * doorstep cell during its install, and once from `installEntrance()` after
   * it. Removing the town's portals alone was not enough: the room->town half
   * lives under the room's scene id, so those were being added a second time.
   */
  world.removePortalsFor(TOWN_ID)
  for (const room of ROOMS) world.removePortalsFor(room.id)
  for (const room of ROOMS) {
    for (const d of room.doors) {
      const from = doorCell(room, d)
      if (d.to === TOWN_ID) {
        world.addPortal({
          id: `${room.id}->town`,
          sceneId: room.id,
          cell: from,
          toSceneId: TOWN_ID,
          toCell: townDoor,
        })
        world.addPortal({
          id: `town->${room.id}`,
          sceneId: TOWN_ID,
          cell: townDoor,
          toSceneId: room.id,
          toCell: from,
        })
        continue
      }
      const other = roomDef(d.to)
      if (other === undefined) continue
      const back = other.doors.find((x) => x.to === room.id)
      if (back === undefined) continue
      world.addPortal({
        id: `${room.id}->${other.id}`,
        sceneId: room.id,
        cell: from,
        toSceneId: other.id,
        toCell: doorCell(other, back),
      })
    }
  }
}
